import * as _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';

export type UseEditable<T> = {
  data: T;
  //  Tells us if the data has been changed from the initial state.
  isDirty: boolean;
  //  This will update the data, and set isDirty to true if it differs from the initial state.
  onUpdate: (newData: Partial<T>) => void;
  //  You'd want to call this when the user saves the data, to reset the isDirty flag.
  onSave: () => void;
  //  This will reset the changes to the initial state, and set isDirty to false.
  onReset: () => void;
};

/**
 * A hook for managing state which can be edited by the user.
 * Solves for a common pattern we have in which we fetch data from
 * the server, and then allow the user to edit it. This hook makes
 * that easier by diffing the initial state and the current state,
 * allowing you to easily determine if the user has made changes.
 *
 * Ensure that your incoming initialState object is stable across renders,
 * by using a useMemo or similar. If you don't, the hook will think the
 * initial state has changed every time the component re-renders.
 *
 * @param {T} initialState - Your initial state. Can be anything, but is somewhat designed to be an object or array.
 *
 * @example
 * const MyComponent = () => {
 *   const asyncValue = useAsyncRequest(fetchSomeData);
 *   const { data, isDirty, onUpdate, onSave, onReset } = useEditable(asyncValue.value);
 *
 *   return (
 *     <div>
 *       {asyncValue.loading ? <Loader /> : (
 *         <LabeledTextInput
 *           label="Name"
 *           value={data.name}
 *           onChange={name => onUpdate({ name })}
 *         />
 *       )}
 *       <Button onClick={onReset} disabled={!isDirty}>Reset</Button>
 *       <Button onClick={onSave} disabled={!isDirty}>Save</Button>
 *     </div>
 *   );
 * };
 */
export default function useEditable<T>(initialState: T): UseEditable<T> {
  const [data, setData] = useState<T>(initialState);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  //  Update the initial state if the reference to it has changed.
  useEffect(() => {
    setData(initialState);
    setIsDirty(false);
  }, [initialState]);

  return {
    data,
    isDirty,
    onUpdate: useCallback((newData: Partial<T>) => {
      setData((prevData) => {
        const updatedData = { ...prevData, ...newData };
        setIsDirty(!_.isEqual(initialState, updatedData));
        return updatedData;
      });
    }, [initialState]),
    onSave: useCallback(() => setIsDirty(false), []),
    onReset: useCallback(() => {
      setData(initialState);
      setIsDirty(false);
    }, [initialState]),
  };
}
